import React, { Suspense, useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import { Toaster } from 'sonner';
import AdSlot from '../ads/AdSlot';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import lazyWithRetry from '../lib/lazyWithRetry';

const lazy = lazyWithRetry;

// Navbar + Hero are the only above-the-fold chunks, so they load eagerly to
// keep LCP fast. Everything below the fold is split into its own chunk and
// is deferred until after the main bundle has parsed — this also delays the
// 6+ content-API requests those components fire on mount, freeing the main
// thread during the critical rendering window.
const Library = lazy(() => import('../components/Library'));
const Premium = lazy(() => import('../components/Premium'));
const DigitalProducts = lazy(() => import('../components/DigitalProducts'));
const About = lazy(() => import('../components/About'));
const Contact = lazy(() => import('../components/Contact'));
const Footer = lazy(() => import('../components/Footer'));
const HomeToolsTeaser = lazy(() => import('../components/HomeToolsTeaser'));

// Reserves vertical space so the lazy section doesn't cause layout shift
// while its chunk streams in.
function SectionSkeleton({ minHeight = '40vh' }) {
  return <div aria-hidden="true" style={{ minHeight }} />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const HOME_PDFS_LIMIT = 24;
const HOME_PRODUCTS_LIMIT = 9;

function Home() {
  const [pdfs, setPdfs] = useState([]);
  const [products, setProducts] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Defer the home data fetches until the browser is idle so they don't
    // compete with the critical render path. The Library/DigitalProducts
    // sections are below the fold and lazy-loaded, so there is no UX cost.
    const run = () => {
      fetchPDFs();
      fetchProducts();
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 1500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const id = setTimeout(run, 400);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    const scrollTo = location.state?.scrollTo;
    if (!scrollTo) return;

    const doScroll = () => {
      const el = document.getElementById(scrollTo);
      if (!el) return false;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
      return true;
    };

    const clearState = () => {
      navigate(location.pathname, { replace: true, state: {} });
    };

    const start = performance.now();
    const MAX_MS = 1200;
    const EPSILON_PX = 6;

    let raf = null;
    const tick = () => {
      const did = doScroll();
      const el = document.getElementById(scrollTo);
      const desiredY = el ? (el.getBoundingClientRect().top + window.pageYOffset - 80) : null;
      const delta = typeof desiredY === 'number' ? Math.abs(window.pageYOffset - desiredY) : null;

      if (did && delta !== null && delta <= EPSILON_PX) {
        clearState();
        return;
      }

      if (performance.now() - start >= MAX_MS) {
        clearState();
        return;
      }

      raf = window.requestAnimationFrame(tick);
    };

    tick();
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [location.state, location.pathname, navigate]);

  const fetchPDFs = async () => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const response = await axios.get(
        `${API}/pdfs?limit=${HOME_PDFS_LIMIT}&offset=0`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setPdfs(response.data);
    } catch (error) {
      console.error('Error fetching PDFs:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=${HOME_PRODUCTS_LIMIT}&offset=0`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      <Navbar />
      <Hero />

      <div className="px-4">
        <div className="max-w-7xl mx-auto pt-6">
          <AdSlot placementKey="home.hero_below" className="mx-auto" />
        </div>
      </div>

      <Suspense fallback={<SectionSkeleton minHeight="80vh" />}>
        <Library pdfs={pdfs} />

        <div className="px-4">
          <div className="max-w-7xl mx-auto -mt-8 pb-8 flex justify-center">
            <Button asChild variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-50">
              <RouterLink to="/pdfs">View all PDFs</RouterLink>
            </Button>
          </div>
        </div>

        <div className="px-4">
          <div className="max-w-7xl mx-auto pb-4">
            <AdSlot placementKey="home.premium_above" className="mx-auto" />
          </div>
        </div>

        <Premium />

        <div className="px-4">
          <div className="max-w-7xl mx-auto pb-4">
            <AdSlot placementKey="home.products_above" className="mx-auto" />
          </div>
        </div>

        <DigitalProducts products={products} />

        <div className="px-4">
          <div className="max-w-7xl mx-auto -mt-10 pb-10 flex justify-center">
            <Button asChild variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-50">
              <RouterLink to="/products">View all digital products</RouterLink>
            </Button>
          </div>
        </div>

        <HomeToolsTeaser />

        <About />
        <Contact />

        <div className="px-4">
          <div className="max-w-7xl mx-auto pb-6">
            <AdSlot placementKey="home.footer_above" className="mx-auto" />
          </div>
        </div>

        <Footer />
      </Suspense>
    </div>
  );
}

export default Home;
